import React, { useState } from 'react';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/money';
import { CheckoutDialog } from './CheckoutDialog';
import { TID } from '@/constants/testIds';

export const CartDrawer = () => {
  const {
    items,
    subtotal,
    shippingFee,
    total,
    open,
    closeCart,
    incItem,
    decItem,
    removeItem,
  } = useCart();

  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => (v ? null : closeCart())}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md flex flex-col p-0 bg-white"
          data-testid={TID.cartDrawer}
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[var(--cw-border)]">
            <SheetTitle className="font-heading text-2xl font-medium">Your cart</SheetTitle>
            <SheetDescription className="text-[var(--cw-muted)]">
              Free shipping on orders over ₹500.
            </SheetDescription>
          </SheetHeader>

          {items.length === 0 ? (
            <div
              className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3"
              data-testid={TID.cartEmpty}
            >
              <div className="w-16 h-16 rounded-full bg-[var(--cw-yellow)] flex items-center justify-center">
                <ShoppingBag className="w-7 h-7 text-[var(--cw-text)]" />
              </div>
              <div className="font-heading text-lg">Your cart is empty</div>
              <p className="text-sm text-[var(--cw-muted)]">
                Add a bar of soap, a chocolate or a hamper to get started.
              </p>
              <button onClick={closeCart} className="cw-btn-secondary mt-2">
                Continue shopping
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 border-b border-[var(--cw-border)] pb-4"
                    data-testid={TID.cartItem(item.id)}
                  >
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-[var(--cw-bg)] flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <div className="flex justify-between items-start gap-2">
                        <div className="font-heading text-sm font-medium">{item.name}</div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-[var(--cw-muted)] hover:text-red-500 transition-colors"
                          aria-label="Remove"
                          data-testid={TID.cartItemRemove(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-sm text-[var(--cw-muted)]">{formatINR(item.price)}</div>
                      {item.customNames && item.customNames.length > 0 && (
                        <div className="text-[11px] text-[var(--cw-muted)] leading-relaxed">
                          <span className="uppercase tracking-[0.12em] font-semibold text-[var(--cw-text)]">Inside · </span>
                          {item.customNames.join(', ')}
                        </div>
                      )}
                      <div className="mt-1 flex items-center justify-between">
                        <div className="inline-flex items-center border border-[var(--cw-border)] rounded-full">
                          <button
                            onClick={() => decItem(item.id)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-[var(--cw-bg)] rounded-full"
                            aria-label="Decrease"
                            data-testid={TID.cartItemDecrement(item.id)}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-7 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => incItem(item.id)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-[var(--cw-bg)] rounded-full"
                            aria-label="Increase"
                            data-testid={TID.cartItemIncrement(item.id)}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="font-heading text-sm font-medium">
                          {formatINR(item.price * item.quantity)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[var(--cw-border)] px-6 py-5 space-y-3 bg-[var(--cw-bg)]">
                <div className="flex justify-between text-sm text-[var(--cw-muted)]">
                  <span>Subtotal</span>
                  <span data-testid={TID.cartSubtotal}>{formatINR(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-[var(--cw-muted)]">
                  <span>Shipping</span>
                  <span>{shippingFee === 0 ? 'Free' : formatINR(shippingFee)}</span>
                </div>
                <div className="flex justify-between font-heading text-lg font-medium pt-2 border-t border-[var(--cw-border)]">
                  <span>Total</span>
                  <span>{formatINR(total)}</span>
                </div>
                <button
                  type="button"
                  className="cw-btn-primary w-full justify-center mt-2"
                  onClick={() => setCheckoutOpen(true)}
                  data-testid={TID.cartCheckoutBtn}
                >
                  Proceed to checkout
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </>
  );
};
